import {ReminderRecurrenceType} from '../../../../../backend/interfaces/reminder-recurrence-type';
import {ReminderResponseType} from '../../../../../backend/enums/reminder-response-type';
import {Reminder} from '../../../../../backend/interfaces/reminder';
import {PostReminder} from '../../../../../backend/requests/post-reminder';

export interface ReminderFormValues {
  title: string;
  message: string;
  date: string;
  time: string;
  responseType: ReminderResponseType;
  recurrenceType: ReminderRecurrenceType;
  recurrenceCount: number;
}

export const sanitizeReminderTitle = (value: string): string => value.trim();

export const sanitizeReminderMessage = (value: string): string =>
  value.replace(/(\r\n|\n|\r)/gm, ' ').trim();

const toDate = (value: Date | string): Date =>
  typeof value === 'string' ? new Date(value) : new Date(value.getTime());

const formatDateInput = (value: Date | string): string => {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatTimeInput = (value: Date | string): string => {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return '';

  const hour = `${date.getHours()}`.padStart(2, '0');
  const minute = `${date.getMinutes()}`.padStart(2, '0');
  return `${hour}:${minute}`;
};

export const getReminderFormValues = (reminder?: Reminder): ReminderFormValues => {
  if (reminder) {
    return {
      title: reminder.title,
      message: reminder.message,
      date: formatDateInput(reminder.startAt),
      time: formatTimeInput(reminder.startAt),
      responseType: reminder.responseType,
      recurrenceType: reminder.recurrenceType,
      recurrenceCount: reminder.recurrenceCount,
    };
  }

  const initialTime = new Date(Date.now() + 5 * 60 * 1000);
  initialTime.setSeconds(0, 0);

  return {
    title: '',
    message: '',
    date: formatDateInput(initialTime),
    time: formatTimeInput(initialTime),
    responseType: ReminderResponseType.YesOrNo,
    recurrenceType: ReminderRecurrenceType.Once,
    recurrenceCount: 1,
  };
};

export const buildReminderPayload = (
  values: ReminderFormValues,
  currentUserId: string,
  seniorId: string,
  existingReminder?: Reminder
): PostReminder => {
  const originalDateTimeIsUnchanged =
    existingReminder &&
    formatDateInput(existingReminder.startAt) === values.date &&
    formatTimeInput(existingReminder.startAt) === values.time;
  const startAt = originalDateTimeIsUnchanged
    ? toDate(existingReminder.startAt)
    : new Date(`${values.date}T${values.time}:00`);

  return {
    fromUserId: existingReminder?.fromUserId ?? currentUserId,
    toUserId: existingReminder?.toUserId ?? seniorId,
    isActive: existingReminder?.isActive ?? true,
    title: sanitizeReminderTitle(values.title),
    message: sanitizeReminderMessage(values.message),
    responseType: values.responseType,
    startAt,
    recurrenceType: values.recurrenceType,
    recurrenceCount:
      values.recurrenceType === ReminderRecurrenceType.Once ? 1 : values.recurrenceCount,
  };
};
